import { InjectionToken, Type } from "@angular/core";
import { FuseNavigationItem } from "@fuse/components/navigation/navigation.types";
import type { FormlyFieldConfig } from "@ngx-formly/core";
import type { DataFormEvent, DataFormModel, DataFormSubmitResponse } from "@shared/components/data-form/data-form.model";
import type { GalleryMedia } from "@shared/components/gallery/gallery.types";
import type { FormDrawerRequest } from "@shared/components/page-layout/drawers/form-drawer/form-drawer.type";
import type { LayoutDrawerComponent } from "@shared/components/page-layout/drawers/layout-drawer.component";
import type { LayoutHeaderComponent } from "@shared/components/page-layout/headers/layout-header.component";
import type { AdvanceFilterData, InvoiceLineItemDetailView, ProjectImportDrawerContext, Question, ValuationProtectionValue } from "@shared/models/domain/custom-views.model";
import { AdminUserView, DriverAvailabilityView, JobWaypointView, StorageItemInstanceView, UpchargesView, WebsiteTeamsView } from "@shared/models/domain/domain-view.model";
import { Medium, Quote, Setting, UserBankAccount, UserCreditCard } from "@shared/models/domain/domain.model";
import { Project, ProjectDeliveryRequest, ProjectImport, Subscription } from "@shared/models/domain/wms.model";
import { EventRequest } from "@shared/models/request/event-request.model";
import { UserCreditCardRequest } from "@shared/models/request/user-credit-card-request.model";
import { SelectOption } from "@shared/models/shared/select-option.model";
import type { JobDetailView } from "@shared/models/views/job-detail-view.model";
import { SubscriptionDetailView } from "@shared/models/views/subscription-view.model";

export type ParentDrawerSubjectEvent = DataFormEvent & { component?: Type<LayoutDrawerComponent> };

export type ParentDrawerCustomEventModel = DataFormModel | UserCreditCard | UserCreditCardRequest | UserBankAccount | Project | ProjectImport | ValuationProtectionValue;

export interface ParentDrawerCustomEvent {
    formUrl?: string;
    component?: Type<LayoutDrawerComponent>;
    model?: ParentDrawerCustomEventModel;
    quoteId?: string;
    quoteNumber?: string;
    instance?: StorageItemInstanceViewWithModel;
    selectedMedias?: GalleryMedia[];
    data?: AdvanceFilterData;
    apiUrl?: string;
    skipApi?: boolean;
    idField?: string;
    dictionary?: Record<string, SelectOption<string | number | object>[]>;
    formFields?: FormlyFieldConfig[];
    description?: string;
    addExtension?: boolean;
    includeFieldsInPayload?: string[];
    job?: JobDetailView;
    waypoint?: JobWaypointView;
    manageDriver?: boolean;
    manageWaypoint?: boolean;
    manageStatus?: boolean;
    isAdmin?: boolean;
    upcharges?: UpchargesView[];
    managePromoCode?: boolean;
    isNoChargeJob?: boolean;
    settings?: Setting[];
    isDepartureMediaOnly?: boolean;
    addProjectFolder?: boolean;
    initialData?: AdvanceFilterData;
    showInspectionStatus?: boolean;
    showCluster?: boolean;
    partnerOptions?: SelectOption[];
    isScreenSmall?: boolean;
    inspectionStatusOptions?: SelectOption[];
    clusterOptions?: SelectOption[];
    smalldrawer?: boolean;
    reload?: boolean;
}

export type ParentDrawerEvent =
    DataFormEvent
    | ParentDrawerSubjectEvent
    | ParentDrawerCustomEvent
    | FormDrawerRequest
    | EventRequest
    | UserBankAccount
    | UserCreditCard
    | DriverAvailabilityView
    | File[]
    | BankAccountDrawerData
    | CreditCardDrawerData
    | SubscriptionSidemarkDrawerData
    | SelectSidemarkDrawerData
    | ReceiveDamagedDrawerData
    | ValuationProtectionDrawerData
    | TeamTerritoryDrawerData
    | QuoteInfoDrawerData
    | InvoiceLineItemDrawerData
    | QuestionDrawerData
    | ProjectImportDrawerData
    | TrackingHistoryDrawerData
    | WarehouseLocationImportDrawerData;

export interface BankAccountDrawerData {
    isCustomer?: boolean;
    userBankAccount?: UserBankAccount;
    projects?: Project[];
    userId?: string;
    noObservableEmit?: boolean;
    userData?: AdminUserView;
}

export interface CreditCardDrawerData {
    isCustomer?: boolean;
    assignToAllJobs?: boolean;
    userCreditCard?: UserCreditCard;
    isPublicPage?: boolean;
    projectId?: string;
    tokenHash?: string;
    projects?: Project[];
    userId?: string;
}

export interface SubscriptionSidemarkDrawerData {
    projectId?: string;
    originalProject?: Partial<Project>;
    prefillSubscription?: Partial<Subscription>;
    prefillModel?: Project;
}

export interface SelectSidemarkDrawerData {
    subscriptionDetail?: SubscriptionDetailView;
    isAdmin?: boolean;
    isDeparture?: boolean;
    currentSidemarkId?: string;
}

export interface ReceiveDamagedDrawerData {
    instance?: StorageItemInstanceViewWithModel;
    medias?: GalleryMedia[];
}

export type TeamTerritoryDrawerData = Partial<Pick<WebsiteTeamsView, 'territoryId' | 'websiteId'>>;

export interface QuoteInfoDrawerData {
    model?: Quote;
    showOnlySidemark?: boolean;
    disableSidemark?: boolean;
    projectImportId?: string;
    isProjectImport?: boolean;
    quoteType?: number;
    quoteNumber?: string;
    preselectedSalesRepId?: string;
    preselectedUser?: AdminUserView;
}

export interface InvoiceLineItemDrawerData {
    lineItem?: InvoiceLineItemDetailView;
}

export interface QuestionDrawerData {
    model?: Partial<Question> & { addNewQuestion?: boolean; sourceSlug?: string };
}

export interface ProjectImportDrawerData {
    isQuoteImport?: boolean;
    prefillProjectImport?: ProjectImportDrawerContext;
}

export interface TrackingHistoryDrawerData {
    orderTrackingId: string;
    trackingNumber: string;
    isOffice: boolean;
}

export interface WarehouseLocationImportDrawerData {
    warehouseId: string;
}

export interface ValuationProtectionDrawerData {
    model: { initialValue: number | null; websiteId: number | null };
}

export interface ParentDrawerRequest<TData = ParentDrawerEvent> {
    component: Type<LayoutDrawerComponent>;
    smallDrawer?: boolean | null;
    mediumDrawer?: boolean | null;
    title?: string | null;
    data?: TData;
}

export interface ParentHeaderData {
    title?: string;
}

export interface ParentHeaderRequest<TData = ParentHeaderData> {
    component: Type<LayoutHeaderComponent>;
    data: TData;
    backLink?: string[];
    backLabel?: string | false;
    noBackButton?: boolean;
}


export interface ParentDrawerUploadRequest {
    waypointId?: number;
    uploadType: string;
    multiple: boolean;
    row?: Record<string, string | number | boolean>;
}

export interface Media extends Medium {
    fullUrl: string;
    storageItemInstanceId?: string;
    subscriptionMediaId?: string;
}

export interface ParentDrawerUploadResponse {
    request: ParentDrawerUploadRequest;
    medias: Media[];
    waypointId?: number;
}

export interface PageLayoutSettings {
    prefix: string;
    replaceUrl?: boolean;
    nav?: FuseNavigationItem;
    hideNav?: boolean;
}

export type PageLayoutContext = Record<string, PageLayoutSettings>;

export const PAGE_SETTINGS = new InjectionToken<PageLayoutContext>('pageSettings');

export interface ParentDrawerCloseData {
    closeDrawer: boolean;
}

export interface ProjectDeliveryRequestListDrawerData {
    model?: {
        requests?: ProjectDeliveryRequest[];
    };
}

export interface QuestionDrawerAnswersData {
    model?: {
        questions?: string;
        isReadonly?: boolean;
    };
}

export interface BeginJobDrawerData {
    reload: boolean;
}

export type StorageItemInstanceViewWithModel = StorageItemInstanceView & { model?: StorageItemInstanceView };

export type WarehouseSettingSubmitResponse = DataFormSubmitResponse & ParentDrawerCustomEvent;

export type StorageEventDrawerEvent = DataFormEvent & Partial<EventRequest> & Partial<Pick<ParentDrawerRequest, 'component'>>;
